import React, { useState, useEffect } from "react";
import BackendProductions from "./BackendProductions";
import ProductionsTable from "./ProductionsTable";
import useProductionsLogic from "./useProductionsLogic";
import useTooltipLogic from "./useTooltipLogic";
import DeliveryMethods from "../Information/DeliveryMethods";
import { getShortRegionName } from "../../projectProducts/regionUtils";
import { buildGazCheapestHint } from "../Offer/gazCheapestOption";
import { buildGazRequestedBrandHint } from "../Offer/gazRequestedBrandHint";

import "../../theme/redesign/tokens.css";
import "../../theme/redesign/section.css";
import "../../theme/redesign/delivery.css";
import "../../theme/redesign/productions.css";

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;

function Productions({
  products,
  setProducts,
  paymentMethod,
  deliveryMethod,
  setDeliveryMethod,
  addressAndCoordinates,
  selfPickUp,
  setComparisons,
  comparisons,
  setSelectedOffer,
  offerRef,
  selectedRegion,
  isMskGazobetonScope = false,
  // В сравнение производств допродажи не входят: оно считается по строкам
  // «Товаров». Признак приходит сверху и нужен только оговорке над таблицей.
  hasGazUpsells = false,
  setGazCheapestHint,
  setGazBrandHint,
  selectProductionRef,
  isNonmetallicMode,
  dealId,
  clientName,
  deliveryDate,
  addressValidationError,
  setAddressValidationError,
  setShowScrollButton,
  productErrors,
  setProductErrors,
  setExcludedByPaymentMethod,
  isCatMode,
}) {
  const [izolifeSettings, setIzolifeSettings] = useState({ settings: [], deliveryCostByRegion: {}, truckPalletsByProduct: {} });
  const [deliveryListExpanded, setDeliveryListExpanded] = useState(false);

  useEffect(() => {
    fetch(`${PATH_TO_BACKEND}/api/izolife-settings`)
      .then(r => r.json())
      .then(result => {
        if (result.success) {
          setIzolifeSettings({
            settings: result.settings || [],
            deliveryCostByRegion: result.deliveryCostByRegion || {},
            truckPalletsByProduct: result.truckPalletsByProduct || {},
          });
        }
      })
      .catch(() => { });
  }, []);

  const selectedRegionShortName = getShortRegionName(selectedRegion);
  const normalizedAddress = String(addressAndCoordinates?.address || '').toLowerCase();
  const isMoscowAddress = normalizedAddress.includes('москва');
  const isMoscowRegionForComparison = selectedRegionShortName === 'МСК' || isMoscowAddress;

  const {
    isValidInput,
    groupedData,
    sortedGroupedData,
    hasValidDescriptions,
    minDistanceKadToClient,
    handleSelectOffer,
    selectedComparisonId,
    isLoading,
    setIsLoading,
  } = useProductionsLogic(
    products,
    selfPickUp,
    comparisons,
    setComparisons,
    setSelectedOffer,
    offerRef,
    izolifeSettings,
    setProducts,
    isMoscowRegionForComparison,
    setShowScrollButton,
    isMskGazobetonScope
  );

  const { tooltip, handleMouseEnter, handleMouseLeave, tableContainerRef, tooltipRef } =
    useTooltipLogic();

  // Проверяем, есть ли калькулятор "Общестрой" в comparisons или products
  const hasObshchestroyCalculator = comparisons.some(comp =>
    comp.materials && comp.materials.some(mat => mat.calculator === "Общестрой")
  );

  // При самовывозе, если все записи "регламент", то показываем "Отправить запрос"
  const shouldShowRequestLink = selfPickUp && sortedGroupedData.length === 0 && hasObshchestroyCalculator;
  const gazDeliveryCategoryNames = new Set([
    'бордюр',
    'бордюр дорожный',
    'брусчатка',
    'брусчатка и тротуарная плитка',
    'плитка тротуарная',
    'газонная решетка',
    'лоток водоотводный жби',
    'лоток водоотводный тротуарный',
  ]);
  const isGazDeliveryCategory = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    return normalized.includes('газобетон') || gazDeliveryCategoryNames.has(normalized);
  };
  const hasGazobetonProduct = (products || []).some((product) => {
    const calculator = String(product?.calculatorCategory || product?.calculator || '').toLowerCase();
    const category = String(product?.category || product?.CATEGORY || '').toLowerCase();
    const name = String(product?.name || '').toLowerCase();
    return isGazDeliveryCategory(calculator) || isGazDeliveryCategory(category) || name.includes('газобетон');
  });
  const shouldShowKadDistance = minDistanceKadToClient !== null && !(isMoscowRegionForComparison && hasGazobetonProduct);

  // Лучший вариант — первая строка отсортированного списка с реальным total > 0.
  const bestEntry = sortedGroupedData.find((entry) => entry && Number(entry.totalSum) > 0) || null;

  // Подсказка «есть вариант дешевле» считается здесь, где таблица сравнения уже
  // собрана: своей цены не считается, берутся её же итоги. Блок расчёта только
  // показывает готовый результат, производство при этом не переключается.
  const gazCheapestHint = buildGazCheapestHint({
    rows: sortedGroupedData,
    selectedProduction: selectedComparisonId,
    isMskGazobetonScope,
  });
  const gazCheapestHintKey = gazCheapestHint ? JSON.stringify(gazCheapestHint) : null;

  useEffect(() => {
    if (typeof setGazCheapestHint !== 'function') {
      return;
    }
    setGazCheapestHint(gazCheapestHintKey ? JSON.parse(gazCheapestHintKey) : null);
  }, [gazCheapestHintKey, setGazCheapestHint]);

  // Завод, который менеджер искал в «Товарах». Считается там же, где собрана
  // таблица: своих цен не считается, берутся её же итоги.
  const gazBrandHint = buildGazRequestedBrandHint({
    rows: sortedGroupedData,
    products,
    selectedProduction: selectedComparisonId,
    isMskGazobetonScope,
  });
  const gazBrandHintKey = gazBrandHint ? JSON.stringify(gazBrandHint) : null;

  useEffect(() => {
    if (typeof setGazBrandHint !== 'function') {
      return;
    }
    setGazBrandHint(gazBrandHintKey ? JSON.parse(gazBrandHintKey) : null);
  }, [gazBrandHintKey, setGazBrandHint]);

  // Переключение на выбранный завод — тем же путём, что и нажатие на строку
  // сравнения: третий аргумент помечает выбор ручным, чтобы автовыбор не вернул
  // дешевейший вариант обратно. Четвёртый гасит прокрутку: кнопки выбора стоят в
  // самой сводке, экран уже на расчёте, и уводить его вниз незачем.
  useEffect(() => {
    if (!selectProductionRef) {
      return undefined;
    }
    selectProductionRef.current = (comparisonId) => {
      const entry = (sortedGroupedData || []).find((row) => row?.comparison?.id === comparisonId);
      if (entry) {
        handleSelectOffer(entry.comparison, entry, true, false);
      }
    };
    return () => {
      selectProductionRef.current = null;
    };
  });

  return (
    <>
      <section className="rd-section" id={isMskGazobetonScope ? "gaz-productions-compare" : undefined}>
        <BackendProductions
          products={products}
          isMskGazobetonScope={isMskGazobetonScope}
          paymentMethod={paymentMethod}
          deliveryMethod={deliveryMethod}
          addressAndCoordinates={addressAndCoordinates}
          selfPickUp={selfPickUp}
          setComparisons={setComparisons}
          comparisons={comparisons}
          setIsLoading={setIsLoading}
          selectedRegion={selectedRegion}
          isNonmetallicMode={isNonmetallicMode}
          setAddressValidationError={setAddressValidationError}
          productErrors={productErrors}
          setProductErrors={setProductErrors}
          setExcludedByPaymentMethod={setExcludedByPaymentMethod}
        />

        <div className="rd-sec-head">
          <h2>
            Сравнение стоимости производств
            {shouldShowKadDistance && (
              <small>Расстояние от&nbsp;КАДа: <b>{minDistanceKadToClient} км</b></small>
            )}
          </h2>
          {/* Внутри МСК Газобетона выбор разгрузки переехал в раздел «Товары»,
              поэтому здесь ни подписи, ни второго переключателя нет. Сам
              DeliveryMethods остаётся смонтированным ниже: он подставляет значения
              по умолчанию и грузит справочники, от которых зависит расчёт.
              Вне области колонка работает как раньше. */}
          {!selfPickUp && (
            <div className="rd-delivery__methods-col rd-sec-head__methods">
              <div className="rd-delivery__methods-head">
                {!isMskGazobetonScope && (
                <span className="rd-delivery__label">
                  {hasGazobetonProduct ? "Способ разгрузки" : "Способ доставки"}
                </span>
                )}
                {!hasGazobetonProduct && (
                  <button
                    type="button"
                    className="rd-delivery__toggle"
                    onClick={() => setDeliveryListExpanded((prev) => !prev)}
                    aria-expanded={deliveryListExpanded}
                    aria-label={deliveryListExpanded ? "Свернуть список" : "Раскрыть список категорий"}
                    title={deliveryListExpanded ? "Свернуть" : "Раскрыть"}
                  >
                    <span
                      className={`rd-delivery__toggle-chevron${deliveryListExpanded ? " is-open" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                )}
              </div>
              {(hasGazobetonProduct || deliveryListExpanded) && (
                <DeliveryMethods
                  deliveryMethod={deliveryMethod}
                  setDeliveryMethod={setDeliveryMethod}
                  selectedRegion={selectedRegion}
                  products={products}
                  forceExpanded
                  hideUnloadControl={isMskGazobetonScope}
                />
              )}
            </div>
          )}
        </div>

        <ProductionsTable
          isMskGazobetonScope={isMskGazobetonScope}
          selectedComparisonId={selectedComparisonId}
          paymentMethod={paymentMethod}
          hasGazUpsells={hasGazUpsells}
          isValidInput={isValidInput}
          isLoading={isLoading}
          sortedGroupedData={sortedGroupedData}
          hasValidDescriptions={hasValidDescriptions}
          selfPickUp={selfPickUp}
          handleSelectOffer={handleSelectOffer}
          handleMouseEnter={handleMouseEnter}
          handleMouseLeave={handleMouseLeave}
          tooltip={tooltip}
          tableContainerRef={tableContainerRef}
          tooltipRef={tooltipRef}
          products={products}
          hasObshchestroyCalculator={hasObshchestroyCalculator}
          shouldShowRequestLink={shouldShowRequestLink}
          dealId={dealId}
          clientName={clientName}
          addressAndCoordinates={addressAndCoordinates}
          selectedRegion={selectedRegion}
          deliveryDate={deliveryDate}
          isCatMode={isCatMode}
        />
      </section>

      {/* Внутри МСК Газобетона лучший вариант отмечается прямо в строке таблицы,
          поэтому нижняя плашка не дублируется. Остальным направлениям она
          показывается как в базовой версии. */}
      {bestEntry && !selfPickUp && !isMskGazobetonScope && (
        <div className="rd-hero-summary">
          <div className="rd-hero-summary__lbl">
            Лучший вариант
            <b>{bestEntry.production}</b>
          </div>
          <div className="rd-hero-summary__val">
            {Math.ceil(Number(bestEntry.totalSum)).toLocaleString("ru-RU")}
            <span className="rd-hero-summary__cur">₽</span>
          </div>
        </div>
      )}
    </>
  );
}

export default Productions;
